import {useState, useEffect, useRef} from 'react';
import {defineMessages, useIntl} from 'react-intl';
import {view} from '@sail/ui';
import {
  AgentCall,
  Conversation,
  StripeAssistantLiveAnswersSource,
} from '@stripe-internal/conversations-client';
import {LoadingTextAsync} from '../../Conversation/LoadingTextAsync';
import {useAnalytics} from '../../AnalyticsProvider';
import {
  InlineLinkClickHandler,
  InlineLinkProps,
} from './InlineLinkClickHandler';
import {MarkdownText} from './MarkdownText';
import {StreamingBuffer} from '../../lib/StreamingBuffer';
import {SourcesDropdown} from './SourcesDropdown';
import {AgentCard} from './AgentCard';

const messages = defineMessages({
  streaming_failed: {
    id: 'conversations_ui.live_answers_response.streaming_failed',
    description:
      'Response text shown to users when an error occurs returning a response',
    defaultMessage: 'Sorry, an error occurred while processing your question.',
  },
  was_halted: {
    id: 'conversations_ui.live_answers_response.was_halted',
    description:
      'Response text shown to users when we stopped streaming due to blocked content',
    defaultMessage:
      "I wasn't able to answer this question. Feel free to ask another or try rephrasing it for better results.",
  },
  long_running: {
    id: 'conversations_ui.live_answers_response.long_running',
    description:
      'Text shown to users when a response is taking longer than usual. Only shown when the question is routed to the payments performace subagent.',
    defaultMessage: 'Working on it—this can take up to a minute.',
  },
});

export type FullyAsyncResponseProps = {
  conversation: Conversation;
  llmConversationId: string;
  messageId: string;
  cacheUuid: string;
  saResponseId: string;
  cachedContent?: string | null;
  sources?: Array<StripeAssistantLiveAnswersSource>;
  bufferChunkSize?: number;
  agentCallRenderer?: (agentCall: AgentCall) => JSX.Element | null;
  onComplete?: (
    content: string,
    answerType?: 'unanswerable' | 'answerable',
    isCanceled?: boolean,
  ) => void;
  onError?: () => void;
};

const STREAMING_POLL_INTERVAL_MS = 500;
const BUFFER_FLUSH_INTERVAL_MS = 20;
const BUFFER_CHUNK_SIZE = 5;
const MIN_PROGRESS_UPDATE_INTERVAL_MS = 5000; // only used for the payments performance subagent

export function useLatest<T>(value: T) {
  const ref = useRef(value);
  useEffect(() => {
    ref.current = value;
  });
  return ref;
}

export const FullyAsyncResponse = ({
  conversation,
  llmConversationId,
  messageId,
  cacheUuid,
  saResponseId,
  cachedContent,
  sources,
  bufferChunkSize = BUFFER_CHUNK_SIZE,
  agentCallRenderer,
  onComplete,
  onError,
}: FullyAsyncResponseProps) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [showSources, setShowSources] = useState<boolean>(false);
  const [completed, setCompleted] = useState<boolean>(false);
  const [cancelled, setCancelled] = useState<boolean>(false);
  const [content, setContent] = useState<string>('');
  const [lastAgentCall, setLastAgentCall] = useState<AgentCall | null>(null);
  const [hasCheckedCache, setHasCheckedCache] = useState<boolean>(false);
  const [progressUpdate, setProgressUpdate] = useState<string | null>(null);
  const [isPreparingResponse, setIsPreparingResponse] =
    useState<boolean>(false);
  const [isLongRunning, setIsLongRunning] = useState<boolean>(false);
  const lastProgressUpdateTimeRef = useRef<number>(0);
  const [streamedSources, setStreamedSources] = useState<
    Array<StripeAssistantLiveAnswersSource>
  >(sources || []);

  // `useLatest` allows the user to pass in unstable functions and we will
  // always be able to access the latest one in hooks that require stability.
  // This would not work with advanced suspense features but we aren't using
  // any of them and don't plan to
  const onCompleteRef = useLatest(onComplete);
  const onErrorRef = useLatest(onError);

  const analytics = useAnalytics();
  const intl = useIntl();

  const resetStateWithContent = (str: string) => {
    setContent(str);
    setLoading(false);
    setShowSources(true);
    setCompleted(true);
    setIsPreparingResponse(false);
    setProgressUpdate(null);
    lastProgressUpdateTimeRef.current = 0;
  };

  // This is an optimization to avoid triggering a bunch of request in the
  // effect below even when we have been provided cached content. We want to
  // both respect `cachedContent` on initial render as well as in the future if
  // it ever changes. The effect below would achieve that, but because effects
  // would run first without the cache we would trigger a network request which
  // would be immediately cancelled once we updated state. Doing this eagerly
  // avoids that.
  if (!hasCheckedCache) {
    setHasCheckedCache(true);
    if (cachedContent != null) {
      resetStateWithContent(cachedContent);
    }
  }

  useEffect(() => {
    if (cachedContent != null) {
      resetStateWithContent(cachedContent);
    }
  }, [cachedContent]);

  useEffect(() => {
    const {client} = conversation;

    // No need to stream if it's already completed
    if (completed) {
      return;
    }

    let wasAnswerable = true;
    const controller = new AbortController();

    const buffer = new StreamingBuffer({
      chunkSize: bufferChunkSize,
      flushIntervalMs: BUFFER_FLUSH_INTERVAL_MS,
      onFlush: (c: string) => {
        setContent(c);
      },
      onComplete: (c: string) => {
        setCompleted(true);
        onCompleteRef.current?.(
          c,
          wasAnswerable ? 'answerable' : 'unanswerable',
        );
      },
    });

    let nextOffset = 0;

    const updateProgressUpdateWithMinTime = (
      newProgressUpdate: string | null,
      longRunning: boolean,
    ) => {
      if (newProgressUpdate === null) {
        setProgressUpdate(null);
        lastProgressUpdateTimeRef.current = 0;
        return;
      }

      // Apply throttling logic only for the payments performance subagent
      if (!longRunning) {
        setProgressUpdate(newProgressUpdate);
        return;
      }

      const now = Date.now();
      const timeSinceLastUpdate = now - lastProgressUpdateTimeRef.current;

      const shouldThrottle =
        lastProgressUpdateTimeRef.current !== 0 &&
        timeSinceLastUpdate < MIN_PROGRESS_UPDATE_INTERVAL_MS;

      if (!shouldThrottle) {
        setProgressUpdate(newProgressUpdate);
        lastProgressUpdateTimeRef.current = now;
      }
    };

    let timeout: ReturnType<typeof setTimeout> | undefined;
    async function getNextChunk() {
      const res = await client.streamText({
        conversationId: conversation.id,
        // Effect ids are always the same as message ids
        effectId: messageId,
        streamId: llmConversationId,
        cacheUuid,
        saResponseId,
        offset: nextOffset,
        signal: controller.signal,
      });

      if (controller.signal.aborted) {
        return;
      }

      setLoading(false);

      if (res.code === 200 && res.data?.type === 'success') {
        const {data} = res;

        nextOffset = data.next_offset;
        setIsPreparingResponse(data.status === 'preparing_response');
        const longRunning = data.is_long_running ?? false;
        setIsLongRunning(longRunning);

        if (data && data.status === 'preparing_response') {
          if (data.progress_update) {
            updateProgressUpdateWithMinTime(data.progress_update, longRunning);
          }
        } else {
          updateProgressUpdateWithMinTime(null, longRunning);
          buffer.push(data.content);
        }

        // Update sources if they're provided in the streaming response
        if (data.sources && data.sources.length > 0) {
          setStreamedSources(data.sources);
        }

        if (data.is_complete || data.was_halted || data.not_answerable) {
          if (data.was_halted) {
            setContent('');
            setCancelled(true);
            setShowSources(false);
            buffer.cancel();

            // TODO: Unclear if this should be a success or error
            // event. We need to fire an event in this case though
            // regardless
            onCompleteRef.current?.('', 'unanswerable', true);
          } else {
            wasAnswerable = !data.not_answerable;
            buffer.complete();
            setShowSources(!data.not_answerable);

            if (data.agent_calls && data.agent_calls.length > 0) {
              setLastAgentCall(data.agent_calls[data.agent_calls.length - 1]);
            }
          }
        } else {
          timeout = setTimeout(getNextChunk, STREAMING_POLL_INTERVAL_MS);
        }
      } else {
        buffer.cancel();
        setCompleted(true);
        setContent('');
        onErrorRef.current?.();
      }
    }

    getNextChunk();

    return () => {
      controller.abort();
      buffer.cancel();
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [
    analytics,
    bufferChunkSize,
    conversation,
    cacheUuid,
    saResponseId,
    cachedContent,
    llmConversationId,
    messageId,
    completed,
    onCompleteRef,
    onErrorRef,
  ]);

  const trimmedContent = content.trim();

  // It's possible to be in the completed state but with no content to
  // show. This happens when an error happens, which is handled
  // outside of this component. In that case we don't want to render
  // anything, even an empty div, which could cause layout issues
  if (completed && trimmedContent === '' && !cancelled) {
    return null;
  }

  return (
    <view.div css={{stack: 'y', gap: 'medium'}}>
      {(loading || isPreparingResponse) && isLongRunning ? (
        <view.div css={{color: 'subdued'}}>
          {intl.formatMessage(messages.long_running)}
        </view.div>
      ) : null}
      {!isPreparingResponse && trimmedContent !== '' ? (
        <InlineLinkClickHandler
          onClick={({href, innerText, parentInnerText}: InlineLinkProps) => {
            analytics.action(
              'universal_help_live_answers_frontend_inline_link_clicked',
              {
                live_answers_source_url: href,
                live_answers_source_title: innerText,
                live_answers_source_context: parentInnerText,
              },
            );
          }}
        >
          <MarkdownText body={trimmedContent} />
        </InlineLinkClickHandler>
      ) : null}
      {completed && lastAgentCall ? (
        <view.div css={{marginTop: '-4px'}}>
          {agentCallRenderer?.(lastAgentCall) || (
            <AgentCard
              agentCall={lastAgentCall}
              conversation={conversation}
              messageId={messageId}
            />
          )}
        </view.div>
      ) : null}
      {cancelled ? <div>{intl.formatMessage(messages.was_halted)}</div> : null}
      {completed &&
      showSources &&
      cachedContent !== '' &&
      streamedSources &&
      streamedSources.length > 0 ? (
        <SourcesDropdown sources={streamedSources} />
      ) : null}
      {loading || isPreparingResponse ? (
        <LoadingTextAsync
          stage="just_one_moment"
          customText={progressUpdate ? `${progressUpdate}...` : ''}
          enableCannedMessageCycling={isLongRunning}
        />
      ) : null}
    </view.div>
  );
};
